import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { BannerComponent } from '../../../../shared/components/banner/banner.component';
import { CardPharmacyComponent } from '../../../../shared/components/card-pharmacy/card-pharmacy.component';
import { PharmacySkeletonComponent } from '../../../../shared/components/pharmacy-skeleton/pharmacy-skeleton.component';
import { SearchAndFiltrerComponent } from '../../../../shared/components/search-and-filtrer/search-and-filtrer.component';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { environment } from '../../../../../environments';
import { CountryStorageService } from '../../../../core/services/country.storage.service';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { Subject, takeUntil } from 'rxjs';

interface Pharmacy {
  id: number;
  name: string;
  address?: string;
  phone?: string;
}

interface PharmaciesResponse {
  results: Pharmacy[];
  pagination: {
    totalItems: number;
    pageCount: number;
    currentPage: number;
    pageSize: number;
  };
}

@Component({
  selector: 'app-list-pharmacies',
  standalone: true,
  templateUrl: './list-pharmacies.component.html',
  styleUrls: ['./list-pharmacies.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    BannerComponent,
    CardPharmacyComponent,
    PharmacySkeletonComponent,
    SearchAndFiltrerComponent,
    RouterModule,
  ],
})
export class ListPharmaciesComponent implements OnInit {
  currentSection: 'medecins' | 'structures' | 'pharmacies' = 'medecins';
  currentTheme = environment.theme.default;
  bannerLinks = [
    { label: 'Portail Public', href: '/' },
    { label: 'Pharmacies', href: undefined },
  ];

  pharmacies: Pharmacy[] = [];
  loading = false;
  error: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Recherche
  searchQuery = '';
  searchTownId = '';
  searchCountryId: any;
  searchSpeciality = '';
  selectedLanguage = '';
  selectedService = '';
  selectedInsurer = '';

  latitude: number | null = null;
  longitude: number | null = null;
  locationActivated = false;

  selectedCountry: Country | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private referentielService: ReferentielService,
    private route: ActivatedRoute,
    private router: Router,
    private countryStorage: CountryStorageService
  ) {
    // Detect route changes to update currentSection
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.router.url.startsWith('/structures')) {
          this.currentSection = 'structures';
        } else if (this.router.url.startsWith('/pharmacies')) {
          this.currentSection = 'pharmacies';
        } else {
          this.currentSection = 'medecins';
        }
      }
    });
  }

  ngOnInit(): void {
    this.countryStorage.country$
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.selectedCountry = country;
        if (country) {
          this.searchCountryId = country.id;
        }
      });

    const defaultCountry = localStorage.getItem('defaultCountry');
    if (defaultCountry) {
      try {
        const countryObj = JSON.parse(defaultCountry);
        this.searchCountryId = countryObj.id;
      } catch {
        this.searchCountryId = null;
      }
    }
    this.loadLocationFromStorage();
    this.loadUrlParams();
    this.loadPharmacies();
  }

  activateLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          this.locationActivated = true;
          // Save to localStorage for persistence
          localStorage.setItem('user_latitude', this.latitude.toString());
          localStorage.setItem('user_longitude', this.longitude.toString());
          localStorage.setItem('location_activated', 'true');
          // Reload pharmacies with location
          this.loadPharmacies();
        },
        (error) => {
          alert("Impossible d'obtenir la localisation.");
        }
      );
    } else {
      alert("La géolocalisation n'est pas supportée par ce navigateur.");
    }
  }

  loadLocationFromStorage() {
    const lat = localStorage.getItem('user_latitude');
    const lng = localStorage.getItem('user_longitude');
    const activated = localStorage.getItem('location_activated');
    if (lat && lng && activated === 'true') {
      this.latitude = parseFloat(lat);
      this.longitude = parseFloat(lng);
      this.locationActivated = true;
    }
  }

  private loadUrlParams(): void {
    this.route.queryParams.subscribe((params) => {
      const newSearchQuery = params['query'] || '';
      const newSearchTownId = params['townId'] || '';
      const newSearchSpeciality = params['specialisationIds'] || '';
      const newSelectedLanguage = params['language'] || '';
      const newSelectedService = params['service'] || '';
      const newSelectedInsurer = params['insurer'] || '';

      // Vérifier si les paramètres ont changé
      const hasParamsChanged =
        this.searchQuery !== newSearchQuery ||
        this.searchTownId !== newSearchTownId ||
        this.searchSpeciality !== newSearchSpeciality ||
        this.selectedLanguage !== newSelectedLanguage ||
        this.selectedService !== newSelectedService ||
        this.selectedInsurer !== newSelectedInsurer;

      // Update values
      this.searchQuery = newSearchQuery;
      this.searchTownId = newSearchTownId;
      this.searchSpeciality = newSearchSpeciality;
      this.selectedLanguage = newSelectedLanguage;
      this.selectedService = newSelectedService;
      this.selectedInsurer = newSelectedInsurer;

      if (hasParamsChanged) {
        this.currentPage = 1;
        this.loadPharmacies();
      } else {
        this.currentPage = parseInt(params['page']) || 1;
      }
    });
  }

  loadPharmacies(): void {
    this.loading = true;
    this.error = null;

    const searchParams = {
      limit: this.pageSize,
      page: this.currentPage,
      query: this.searchQuery || undefined,
      townId: this.searchTownId ? Number(this.searchTownId) : undefined,
      countryId: this.searchCountryId ? this.searchCountryId : undefined,
      specialisationIds: this.searchSpeciality || undefined,
      languageIds: this.selectedLanguage || undefined,
      serviceAvailableIds: this.selectedService || undefined,
      insurerIds: this.selectedInsurer || undefined,
      latitude: this.latitude || undefined,
      longitude: this.longitude || undefined,
    };

    this.referentielService.getPharmacies(searchParams).subscribe({
      next: (response: PharmaciesResponse) => {
        this.pharmacies = response.results;
        this.totalItems = response.pagination.totalItems;
        this.totalPages = response.pagination.pageCount;
        this.currentPage = response.pagination.currentPage;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des pharmacies:', error);
        this.error = 'Erreur lors du chargement des pharmacies';
        this.loading = false;
      },
    });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updateUrlParams();
      this.loadPharmacies();
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.updateUrlParams();
    this.loadPharmacies();
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.currentPage > 1 ? this.currentPage : undefined,
    };

    if (this.searchQuery) {
      queryParams.query = this.searchQuery;
    }
    if (this.searchTownId) {
      queryParams.town = this.searchTownId;
    }
    if (this.selectedLanguage) {
      queryParams.language = this.selectedLanguage;
    }
    if (this.selectedService) {
      queryParams.service = this.selectedService;
    }
    if (this.selectedInsurer) {
      queryParams.insurer = this.selectedInsurer;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(
        1,
        this.currentPage - Math.floor(maxVisiblePages / 2)
      );
      let end = Math.min(this.totalPages, start + maxVisiblePages - 1);
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    return pages;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
